import {
  activeCommentAtom,
  commentsAtom,
} from "@app/shared/atoms/comments-atom";
import { CommentAction } from "@app/shared/components/Comments/CommentAction";
import { CommentAvatar } from "@app/shared/components/Comments/CommentAvatar";
import { CommentForm } from "@app/shared/components/Comments/CommentForm";
import { LoadMorePagination } from "@app/shared/components/Pagination";
import { useCommentPagination } from "@app/shared/hooks";
import { formatRelativeTime } from "@core/shared/utils";
import { trans } from "@mongez/localization";
import { MessageCircleOff } from "lucide-react";

export function CommentsList({
  typeId,
  type,
}: {
  typeId: number;
  type: string;
}) {
  const { comments } = commentsAtom.useValue();

  const activeComment = activeCommentAtom.useValue();

  const { hasNextPage, isLoadingMore, loadMoreComments } = useCommentPagination(
    typeId,
    type,
  );

  return (
    <>
      <div className="space-y-5">
        {comments.length > 0 ? (
          <div className="flex flex-col gap-4">
            {comments.map((comment) => {
              return (
                <div key={comment.id} className="flex gap-3">
                  <CommentAvatar user={comment.createdBy} />
                  <div className="min-w-0 flex-1">
                    <div className="rounded-2xl rounded-se-sm bg-slate-100 px-4 py-3 dark:bg-slate-800">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <h4 className="truncate text-sm font-black text-slate-900 dark:text-white">
                            {comment.createdBy?.name || trans("student")}
                          </h4>
                          <span
                            title={comment.createdAt.text}
                            className="text-xs text-slate-500 dark:text-slate-400"
                          >
                            {formatRelativeTime(comment.createdAt)}
                          </span>
                        </div>
                        <CommentAction
                          comment={comment}
                          type={type}
                          typeId={typeId}
                        />
                      </div>
                      {activeComment?.id === comment.id ? (
                        <div className="mt-3">
                          <CommentForm compact type={type} typeId={typeId} />
                        </div>
                      ) : (
                        <p className="mt-2 whitespace-pre-wrap text-sm leading-6 text-slate-700 dark:text-slate-200">
                          {comment.comment}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
            <LoadMorePagination
              hasNextPage={hasNextPage}
              onLoadMore={loadMoreComments}
              isLoading={isLoadingMore}
            />
          </div>
        ) : (
          <div className="flex flex-col items-center gap-3 py-8 text-center">
            <MessageCircleOff className="h-10 w-10 text-slate-300 dark:text-slate-600" />
            <h3 className="font-black text-slate-600 dark:text-slate-200">
              {trans("noCommentsYet")}
            </h3>
            <p className="text-sm text-slate-400">
              {trans("beFirstToComment")}
            </p>
          </div>
        )}
      </div>
    </>
  );
}
